import { snapToPixel, lerp, clampRange, clamp01 } from '../util/math.js';
import { pathDistance, pointDistance, spotReached } from '../util/interpolation.js';
import { fieldMetrics, orientX, screenXForYard, goalLineXForDirection, ballFrontX, ballBreaksPlane } from '../field/metrics.js';
import { stopReasonForPlay, playHash, playChance, isInterceptionReturn, turnoverReturnYards, visualYardsForPlay, isInterceptionPlay, interceptionHeadline } from './helpers.js';
import { clonePlayers, findPlayer, carriedBallPoint, setPlayersStill, nearestPlayerToPoint } from '../player/model.js';
import { createPathSegment, createHoldSegment, evaluateTrack, evaluateFlowingTrack, compactPathPoints, createMotionState, steerMotionState, projectedPoint, playersFromMotionStates, freezeLiveFrame, cloneLiveFrame, spreadTargetAwayFromPeers, roleSpacingDistance, guidePointAt, curvedApproachTarget } from './track.js';
import { liveFrameCount } from './context.js';
import { playerPixelsPerFrame } from './speed.js';
import { buildRouteShapePoints, buildPostCatchPoints, buildInterceptionReturnPoints, interceptionReturnEndPoint, turnoverDefenderRole, routeMotionSample } from './waypoints.js';
import type { FieldPlayer } from '../player/model.js';

// These helpers still live in app.js and have not yet been extracted.
// They are imported here as module-level declarations so the rest of
// the function compiles.  Replace with real imports once extracted.
import { setPlayerState, centerSnapBallPoint, blendBallPoint } from '../player/model.js';
import { movePointTowards } from '../util/interpolation.js';
import { speedRatingForRole } from './speed.js';
import { buildArcadePassLiveSequence } from './live-pass-arcade.js';

import { routePlan } from './waypoints.js';

function finishFrameBudget(initialGapPx: number, minFrames: number, chasePacePx: number, bufferFrames: number = 8): number {
  return Math.max(minFrames, Math.round(initialGapPx / Math.max(1, chasePacePx)) + bufferFrames);
}

function ballFlightFrameBudget(
  throwDistancePx: number,
  airYards: number,
  catchFrame: number,
  dropFrames: number,
  deepConcept: boolean,
  turnover: boolean
): number {
  const targetBallPixelsPerFrame = deepConcept
    ? 11.5
    : turnover
      ? 12
      : airYards >= 18
        ? 12.5
        : airYards >= 10
          ? 13.5
          : 15;
  const minFrames = deepConcept
    ? 16
    : airYards >= 18
      ? 14
      : airYards >= 10
        ? 11
        : 8;
  return clampRange(
    Math.round(throwDistancePx / Math.max(1, targetBallPixelsPerFrame)),
    minFrames,
    Math.max(minFrames + 1, catchFrame - dropFrames - 2)
  );
}

export function buildPassLiveSequence(context: any): any[] {
  context = context.presentationPlay ? { ...context, play: context.presentationPlay } : context;
  if (context.play?.arcadeReplay?.source === 'arcade-sim-v1' && context.play?.arcadePlaybackMode === 'raw') {
    return buildArcadePassLiveSequence(context);
  }
  const presentationPlan = context.presentationPlan || {};
  const passPresentation = presentationPlan.pass || {};
  const frameCount = liveFrameCount(context.play);
  const terminalReason = stopReasonForPlay(context.play);
  const offenseBase = clonePlayers(context.formation.offense);
  const defenseBase = clonePlayers(context.formation.defense);
  const offenseTracks = new Map();
  const defenseTracks = new Map();
  const xDelta = (value: number) => value * context.direction;
  const depthAlignX = (baseX: number, targetX: number) => context.direction === 1 ? Math.max(baseX, targetX) : Math.min(baseX, targetX);
  const resultText = (context.play.result || "").toLowerCase();
  const sack = resultText.includes("sack");
  const incomplete = terminalReason === "incomplete" || resultText.includes("incomplete");
  const interception = isInterceptionPlay(context.play);
  const turnover = !!context.play.turnover || resultText.includes("interception");
  const scramble = !!context.play.scramble;
  const completedPass = !sack && !incomplete && !turnover && !scramble;
  const interceptionCallout = interception ? interceptionHeadline() : null;
  const passFamily = context.play.passFamily || "";
  const deepConcept = ["play-action-post", "flood-shot", "hail-mary"].includes(context.play.playId)
    || ["shot", "dagger", "hail-mary"].includes(passFamily);
  const extendedMode = presentationPlan.mode === "extended" && (completedPass || turnover || scramble || deepConcept);
  const pressureTiming = context.play.pressureTiming
    || (context.play.pressureLook === "mug" || context.play.pressureLook === "edge"
      ? "trigger"
      : context.play.coverageShell === "two-high" || context.play.coverageShell === "quarters"
        ? "delayed"
        : "balanced");
  const rallyStyle = context.play.rallyStyle
    || (context.play.pursuitStyle === "overplay"
      ? "swarm"
      : context.play.coverageShell === "two-high" || context.play.coverageShell === "quarters"
        ? "vice"
        : "inside-out");
  const tackleEnvironment = context.play.tackleEnvironment
    || (context.play.pressureLook === "mug"
      ? "chaotic"
      : context.play.boxCount >= 8
        ? "crowded"
        : context.play.coverageShell === "two-high" || context.play.coverageShell === "quarters"
          ? "space"
          : "leverage");
  const pursuitSpread = clampRange(passPresentation.pursuitSpread ?? 1, 0.84, 1.18);
  const spacingMultiplier = (tackleEnvironment === "space" ? 1.18 : tackleEnvironment === "crowded" ? 0.86 : tackleEnvironment === "chaotic" ? 0.92 : 1) * pursuitSpread;
  const rallyPursuitMultiplier = (rallyStyle === "swarm" ? 1.12 : rallyStyle === "downhill" ? 1.06 : rallyStyle === "vice" ? 0.92 : 1)
    * clampRange(passPresentation.contactIntensity ?? 1, 0.86, 1.16);
  const triggerFrameShift = pressureTiming === "trigger" ? -2 : pressureTiming === "delayed" ? 2 : 0;
  const visualYards = visualYardsForPlay(context.play);
  const deepCompletion = completedPass
    && terminalReason === "tackle"
    && (
      (context.play.airYards || 0) >= 14
      || (deepConcept && (((context.play.airYards || 0) >= 10) || (visualYards >= 16)))
      || ((context.play.airYards || 0) >= 8 && ((visualYards >= 18) || ((context.play.yacYards || 0) >= 8)))
      || (visualYards >= 22)
      || (extendedMode && ((context.play.airYards || 0) >= 10 || visualYards >= 18))
    );
  const airFactor = clamp01(Math.max(0, (context.play.airYards || 0) - 5) / 24);
  const deepTackleFrames = deepCompletion
    ? clampRange(
        Math.round(
          (10 + airFactor * 4 + Math.min(5, (context.play.yacYards || 0) / 2))
          * (tackleEnvironment === "space" ? 1.18 : tackleEnvironment === "crowded" ? 0.82 : tackleEnvironment === "chaotic" ? 0.92 : 1)
          * clampRange(passPresentation.contactIntensity ?? 1, 0.9, 1.18)
        ),
        8,
        extendedMode ? 20 : 18,
      )
    : 0;
  const dropFrames = clampRange(Math.round(8 + airFactor * 4) + (passPresentation.qbHitchFrames ?? 0), 8, extendedMode ? 16 : 14);
  const qbBase = findPlayer(offenseBase, "QB")!;
  const centerBase = findPlayer(offenseBase, "C") || qbBase;
  const snapFrameCount = clampRange(Math.round(dropFrames * 0.45), 3, 5);
  const qbSetPoint = {
    x: orientX(context.losX, -74 - (passPresentation.qbDepthOffset ?? 0), context.direction),
    y: snapToPixel(context.centerY - 8 + (passPresentation.qbOffsetY ?? 0)),
  };
  const qbThrowPoint = {
    x: orientX(qbSetPoint.x, 8 + Math.max(0, (passPresentation.throwLeadX ?? 6) - 6) * 0.35, context.direction),
    y: snapToPixel(qbSetPoint.y - 2 + (passPresentation.throwLeadY ?? 0) * 0.2),
  };
  const plan = routePlan(context.play, context.losX, context.centerY, context.endX, context.direction);
  const targetRole = plan.targetRole || "WR1";
  const actualCatchYard = context.play.catchYardLine || context.play.passTargetYardLine || context.play.endYardLine || context.play.startYardLine;
  const routeTargetYard = completedPass || turnover
    ? actualCatchYard
    : (context.play.passTargetYardLine || actualCatchYard);
  const catchX = screenXForYard(actualCatchYard, context.direction);
  const routeTargetX = screenXForYard(routeTargetYard, context.direction);
  const routeTargets = Object.fromEntries(
    Object.entries(plan.routes).map(([role, [targetX, targetY]]: [string, any]) => [role, role === targetRole ? [routeTargetX, targetY] : [targetX, targetY]])
  );
  const field = fieldMetrics();
  const catchTarget = routeTargets[targetRole] || [catchX, context.laneY];
  const catchPoint = {
    x: snapToPixel(catchTarget[0] + xDelta(passPresentation.catchOffsetX ?? 0)),
    y: clampRange(snapToPixel(catchTarget[1] + (passPresentation.catchOffsetY ?? 0)), field.fieldTop + 18, field.fieldBottom - 18),
  };
  const defaultPrimaryRole = turnoverDefenderRole(targetRole);
  const interceptorSpeedRole = turnover ? defaultPrimaryRole : null;
  const interceptorBase = interceptorSpeedRole
    ? (findPlayer(defenseBase, interceptorSpeedRole) || defenseBase[0] || null)
    : null;
  const pickPoint = turnover
    ? { x: snapToPixel(catchPoint.x + xDelta(10)), y: snapToPixel(catchPoint.y) }
    : catchPoint;
  const carrierEndPoint = turnover
    ? interceptionReturnEndPoint(context.play, pickPoint, context.endX, context.laneY, context.centerY)
    : { x: context.endX, y: context.laneY };
  const scrambleCommitFrame = scramble
    ? clampRange(
        Math.round(dropFrames + 5 + Math.max(0, (context.play.airYards || 0) / 6)),
        dropFrames + 4,
        Math.max(dropFrames + 4, frameCount - 10)
      )
    : 0;
  const scramblePocketPoint = {
    x: snapToPixel(lerp(qbSetPoint.x, orientX(context.losX, -18, context.direction), 0.6)),
    y: snapToPixel(lerp(qbSetPoint.y, context.laneY, 0.18)),
  };
  const scrambleBreakPoint = {
    x: orientX(context.losX, 8, context.direction),
    y: snapToPixel(lerp(qbSetPoint.y, context.laneY, 0.46)),
  };
  const scramblePath = scramble
    ? compactPathPoints([qbSetPoint, scramblePocketPoint, scrambleBreakPoint, carrierEndPoint])
    : [];
  const carrierGoalLineX = turnover ? goalLineXForDirection(-context.direction) : context.goalLineX;
  const sackSpotPoint = {
    x: snapToPixel(lerp(qbSetPoint.x, carrierEndPoint.x, 0.78)),
    y: snapToPixel(lerp(qbSetPoint.y, carrierEndPoint.y, 0.78)),
  };
  const targetBase = (findPlayer(offenseBase, targetRole) || findPlayer(offenseBase, "WR1") || qbBase)!;
  const targetRoutePoints = buildRouteShapePoints(context.play, targetRole, { x: targetBase.x, y: targetBase.y }, catchPoint, context.losX, context.centerY, context.direction);
  const postCatchPoints = turnover
    ? buildInterceptionReturnPoints(context.play, pickPoint, carrierEndPoint, context.direction, context.centerY)
    : completedPass
      ? buildPostCatchPoints(context.play, catchPoint, carrierEndPoint)
      : [catchPoint, carrierEndPoint];
  const postCatchCarrier = turnover ? interceptorBase : targetBase;
  const catchFrame = sack
    ? 0
    : scramble
      ? clampRange(
          Math.round(pathDistance(targetRoutePoints) / playerPixelsPerFrame(targetBase.speedRating || 78, "pass")),
          scrambleCommitFrame + 4,
          Math.max(scrambleCommitFrame + 5, frameCount - 4)
        )
    : clampRange(
        Math.round(pathDistance(targetRoutePoints) / playerPixelsPerFrame(targetBase.speedRating || 78, "pass")),
        dropFrames + (extendedMode ? 10 : 8),
        Math.max(dropFrames + (extendedMode ? 12 : 10), frameCount - (completedPass || turnover ? (extendedMode ? 11 : 9) : 5))
      );
  const throwDistance = pointDistance(qbThrowPoint, turnover ? pickPoint : catchPoint);
  const flightFrames = sack || scramble
    ? 0
    : ballFlightFrameBudget(
        throwDistance,
        context.play.airYards || 0,
        catchFrame,
        dropFrames,
        deepConcept,
        turnover
      );
  const releaseFrame = sack || scramble
    ? 0
    : clampRange(
        Math.max(dropFrames + 2, catchFrame - flightFrames) + (passPresentation.releaseDelayFrames ?? 0),
        dropFrames + 1,
        Math.max(dropFrames + 2, catchFrame - 2)
      );
  const rushBaseEnd = sack ? Math.max(dropFrames + 5, Math.round(frameCount * 0.62)) : Math.max(dropFrames + 5, Math.round(frameCount * 0.48));
  const rushEnd = clampRange(
    rushBaseEnd + triggerFrameShift + (tackleEnvironment === "chaotic" ? -1 : tackleEnvironment === "space" ? 1 : 0),
    dropFrames + 4,
    Math.max(dropFrames + 5, frameCount - 2),
  );
  const maxYacFrames = Math.max(6, frameCount - catchFrame - (deepCompletion ? deepTackleFrames + 2 : 2) + (extendedMode ? (passPresentation.extendedWindowFrames ?? 0) : 0));
  const postCatchTravelFrames = completedPass || turnover
    ? clampRange(
        Math.round(pathDistance(postCatchPoints) / playerPixelsPerFrame(postCatchCarrier?.speedRating || 78, "pass", turnover || context.play.playId === "screen-left" ? 0.94 : 0.9)),
        6,
        maxYacFrames
      )
    : 0;
  const scrambleRunFrames = scramble
    ? clampRange(
        Math.round(pathDistance(scramblePath) / playerPixelsPerFrame(qbBase.speedRating || 78, "run", 0.84)),
        8,
        Math.max(10, frameCount - scrambleCommitFrame - 2)
      )
    : 0;
  const carrierAdvanceEnd = incomplete
    ? Math.min(frameCount - 1, catchFrame + 4)
    : sack
      ? Math.max(dropFrames + 8, frameCount - 2)
      : scramble
        ? ["touchdown", "out-of-bounds"].includes(terminalReason)
          ? frameCount - 1
          : Math.max(scrambleCommitFrame + 8, Math.min(frameCount - 2, scrambleCommitFrame + scrambleRunFrames))
      : ["touchdown", "out-of-bounds"].includes(terminalReason)
        ? frameCount - 1
        : Math.max(
            catchFrame + 8,
            Math.max(
              Math.min(frameCount - 1 - (deepCompletion ? deepTackleFrames : 1), catchFrame + postCatchTravelFrames + (extendedMode ? (passPresentation.extendedWindowFrames ?? 0) : 0)),
              frameCount - 1 - (deepCompletion ? deepTackleFrames : 3),
            )
          );
  const deepTackleStart = deepCompletion
    ? Math.max(catchFrame + 4, carrierAdvanceEnd - Math.max(4, deepTackleFrames) + (passPresentation.tackleFrameBias ?? 0))
    : frameCount - 1;
  const targetHalf = catchPoint.y < context.centerY - 22 ? "top" : catchPoint.y > context.centerY + 22 ? "bottom" : "middle";
  const tackleSide = String(passPresentation.tackleSide || "");
  const arrivalJitterFrames = passPresentation.arrivalJitterFrames ?? 0;
  const tackleSideSign = tackleSide === "top-shoulder"
    ? -1
    : tackleSide === "bottom-shoulder"
      ? 1
      : targetHalf === "top"
        ? 1
        : targetHalf === "bottom"
          ? -1
          : (playHash(context.play, "pass-tackle-side") % 2 === 0 ? -1 : 1);
  const primaryOutsideHelp = targetHalf === "bottom" ? "S2" : "S1";
  const underneathHelp = targetHalf === "top" ? "LB1" : targetHalf === "bottom" ? "LB3" : "LB2";
  const coverageShellById = ({
    "quick-slant": { primaryRole: defaultPrimaryRole, helpRole: underneathHelp, robberRole: "LB2", rallyRole: primaryOutsideHelp },
    "mesh-cross": { primaryRole: targetRole === "SLOT" ? "S2" : defaultPrimaryRole, helpRole: "LB2", robberRole: targetHalf === "top" ? "S1" : "S2", rallyRole: targetHalf === "top" ? "LB1" : "LB3" },
    "play-action-post": { primaryRole: defaultPrimaryRole, helpRole: primaryOutsideHelp, robberRole: underneathHelp, rallyRole: targetHalf === "top" ? "LB1" : "LB3" },
    "flood-shot": { primaryRole: defaultPrimaryRole, helpRole: targetHalf === "bottom" ? "S2" : "S1", robberRole: "LB2", rallyRole: targetHalf === "bottom" ? "LB3" : "LB1" },
    "screen-left": { primaryRole: "LB3", helpRole: "S2", robberRole: "LB2", rallyRole: "CB2" },
    "hail-mary": { primaryRole: defaultPrimaryRole, helpRole: primaryOutsideHelp, robberRole: targetHalf === "top" ? "CB2" : "CB1", rallyRole: "LB2" },
  } as Record<string, any>);
  const coverageShellByFamily: Record<string, any> = {
    quick: { primaryRole: defaultPrimaryRole, helpRole: underneathHelp, robberRole: "LB2", rallyRole: primaryOutsideHelp },
    mesh: { primaryRole: targetRole === "SLOT" ? "S2" : defaultPrimaryRole, helpRole: "LB2", robberRole: targetHalf === "top" ? "S1" : "S2", rallyRole: targetHalf === "top" ? "LB1" : "LB3" },
    shallow: { primaryRole: defaultPrimaryRole, helpRole: "LB2", robberRole: targetHalf === "top" ? "S1" : "S2", rallyRole: targetHalf === "top" ? "LB1" : "LB3" },
    drive: { primaryRole: defaultPrimaryRole, helpRole: underneathHelp, robberRole: primaryOutsideHelp, rallyRole: "LB2" },
    levels: { primaryRole: defaultPrimaryRole, helpRole: underneathHelp, robberRole: primaryOutsideHelp, rallyRole: "LB2" },
    dig: { primaryRole: defaultPrimaryRole, helpRole: underneathHelp, robberRole: primaryOutsideHelp, rallyRole: "LB2" },
    smash: { primaryRole: defaultPrimaryRole, helpRole: primaryOutsideHelp, robberRole: underneathHelp, rallyRole: targetHalf === "top" ? "LB1" : "LB3" },
    flood: { primaryRole: defaultPrimaryRole, helpRole: targetHalf === "bottom" ? "S2" : "S1", robberRole: "LB2", rallyRole: targetHalf === "bottom" ? "LB3" : "LB1" },
    boot: { primaryRole: defaultPrimaryRole, helpRole: targetHalf === "bottom" ? "S2" : "S1", robberRole: "LB2", rallyRole: targetHalf === "bottom" ? "LB3" : "LB1" },
    screen: { primaryRole: targetRole === "RB" ? "LB3" : targetRole === "TE" ? "LB2" : defaultPrimaryRole, helpRole: targetHalf === "bottom" ? "S2" : "S1", robberRole: "LB2", rallyRole: targetRole === "SLOT" ? "CB2" : primaryOutsideHelp },
    shot: { primaryRole: defaultPrimaryRole, helpRole: primaryOutsideHelp, robberRole: underneathHelp, rallyRole: targetHalf === "top" ? "LB1" : "LB3" },
    dagger: { primaryRole: defaultPrimaryRole, helpRole: primaryOutsideHelp, robberRole: underneathHelp, rallyRole: targetHalf === "top" ? "LB1" : "LB3" },
    "red-zone": { primaryRole: defaultPrimaryRole, helpRole: primaryOutsideHelp, robberRole: "LB2", rallyRole: targetHalf === "top" ? "LB1" : "LB3" },
    "hail-mary": { primaryRole: defaultPrimaryRole, helpRole: primaryOutsideHelp, robberRole: targetHalf === "top" ? "CB2" : "CB1", rallyRole: "LB2" },
  };
  const coverageShell = coverageShellById[context.play.playId]
    || coverageShellByFamily[passFamily]
    || { primaryRole: defaultPrimaryRole, helpRole: primaryOutsideHelp, robberRole: underneathHelp, rallyRole: "LB2" };
  const primaryRole = context.play.participants?.tacklerRole || coverageShell.primaryRole;
  const helpRole = coverageShell.helpRole;
  const robberRole = coverageShell.robberRole;
  const bracketRoles = new Set([primaryRole, helpRole].filter(Boolean));
  const commitRoles = new Set(Array.from(new Set([primaryRole, helpRole, coverageShell.rallyRole].filter(Boolean))));
  const deepTackleRoles = Array.from(new Set([primaryRole, helpRole, coverageShell.rallyRole, robberRole].filter(Boolean)));
  const tackleFinishRoles = deepTackleRoles.length ? deepTackleRoles : [primaryRole, helpRole, coverageShell.rallyRole, robberRole].filter(Boolean);
  const scrambleTackleRoles = Array.from(new Set([primaryRole, helpRole, coverageShell.rallyRole, robberRole, "DE1", "DE2", "LB2"].filter(Boolean)));

  offenseTracks.set("QB", sack
    ? [
        createPathSegment(0, dropFrames, [{ x: qbBase.x, y: qbBase.y }, qbSetPoint], "polyline", true),
        createPathSegment(dropFrames, carrierAdvanceEnd, [qbSetPoint, sackSpotPoint], "polyline", true),
        createHoldSegment(carrierAdvanceEnd, frameCount - 1, sackSpotPoint, false),
      ]
    : scramble
      ? [
          createPathSegment(0, dropFrames, [{ x: qbBase.x, y: qbBase.y }, qbSetPoint], "polyline", true),
          createHoldSegment(dropFrames, Math.max(dropFrames, scrambleCommitFrame - 2), qbSetPoint, false),
          createPathSegment(Math.max(dropFrames, scrambleCommitFrame - 2), carrierAdvanceEnd, scramblePath, "polyline", true),
          createHoldSegment(carrierAdvanceEnd, frameCount - 1, carrierEndPoint, false),
        ]
    : [
        createPathSegment(0, dropFrames, [{ x: qbBase.x, y: qbBase.y }, qbSetPoint], "polyline", true),
        createHoldSegment(dropFrames, Math.max(dropFrames, releaseFrame - 2), qbSetPoint, false),
        createPathSegment(Math.max(dropFrames, releaseFrame - 2), releaseFrame, [qbSetPoint, qbThrowPoint], "polyline", true),
        createHoldSegment(releaseFrame, frameCount - 1, qbThrowPoint, false),
      ]);

  ["WR1", "WR2", "SLOT", "TE", "RB"].forEach((role) => {
    const basePlayer = findPlayer(offenseBase, role);
    if (!basePlayer || !routeTargets[role]) {
      return;
    }
    const routePoint = { x: routeTargets[role][0], y: routeTargets[role][1] };
    const delayFrames = role === "RB" && context.play.playId !== "screen-left"
      ? 3
      : role === "TE" && context.play.playId === "play-action-post"
        ? 2
        : 0;
    const routeStart = delayFrames;
    const routeSample = routeMotionSample(context.play, role);

    if (role === targetRole && completedPass) {
      const routePoints = buildRouteShapePoints(context.play, role, { x: basePlayer.x, y: basePlayer.y }, catchPoint, context.losX, context.centerY, context.direction);
      offenseTracks.set(role, [
        createPathSegment(routeStart, catchFrame, routePoints, routeSample, true),
        createPathSegment(catchFrame, carrierAdvanceEnd, postCatchPoints, "spline", true),
        createHoldSegment(carrierAdvanceEnd, frameCount - 1, postCatchPoints[postCatchPoints.length - 1], false),
      ]);
      return;
    }

    const routePoints = buildRouteShapePoints(context.play, role, { x: basePlayer.x, y: basePlayer.y }, routePoint, context.losX, context.centerY, context.direction);
    const routeFrames = clampRange(
      Math.round(pathDistance(routePoints) / playerPixelsPerFrame(basePlayer.speedRating || 78, "pass", role === "RB" ? 0.92 : 1)),
      routeStart + 8,
      Math.max(routeStart + 8, Math.min(carrierAdvanceEnd, catchFrame ? catchFrame + 8 : carrierAdvanceEnd))
    );
    offenseTracks.set(role, [
      createPathSegment(routeStart, routeFrames, routePoints, routeSample, true),
      createHoldSegment(routeFrames, frameCount - 1, routePoints[routePoints.length - 1], false),
    ]);
  });

  const offenseFlowPlans = new Map<string, {
    lookahead: number;
    settleBlend: number;
    driftSign: number;
    driftScale: number;
    minDrift: number;
    maxDrift: number;
    nearDriftDistance: number;
    farDriftDistance: number;
  }>();

  ["WR1", "WR2", "SLOT", "TE", "RB"].forEach((role) => {
    const basePlayer = findPlayer(offenseBase, role);
    const target = routeTargets[role];
    if (!basePlayer || !target) {
      return;
    }
    const releaseSideSign = Math.sign(
      (target[1] - basePlayer.y)
      || (basePlayer.y < context.centerY ? -1 : basePlayer.y > context.centerY ? 1 : (playHash(context.play, `${role}-release-drift`) % 2 === 0 ? -1 : 1))
    );
    offenseFlowPlans.set(role, {
      lookahead: role === "WR1" || role === "WR2" ? 0.076 : role === "SLOT" ? 0.068 : role === "TE" ? 0.06 : 0.05,
      settleBlend: role === "WR1" || role === "WR2" ? 0.6 : role === "SLOT" ? 0.58 : role === "TE" ? 0.54 : 0.48,
      driftSign: releaseSideSign,
      driftScale: role === "WR1" || role === "WR2" ? 0.44 : role === "SLOT" ? 0.34 : role === "TE" ? 0.24 : 0.14,
      minDrift: role === "WR1" || role === "WR2" ? 0.7 : role === "SLOT" ? 0.55 : 0.35,
      maxDrift: role === "WR1" || role === "WR2" ? 3.4 : role === "SLOT" ? 2.6 : role === "TE" ? 1.9 : 1.2,
      nearDriftDistance: role === "RB" ? 6 : 8,
      farDriftDistance: role === "WR1" || role === "WR2" ? 46 : role === "SLOT" ? 38 : 28,
    });
  });

  ["LT", "LG", "C", "RG", "RT"].forEach((role) => {
    const basePlayer = findPlayer(offenseBase, role);
    if (!basePlayer) {
      return;
    }
    const settlePoint = { x: orientX(context.losX, -10, context.direction), y: basePlayer.y };
    offenseTracks.set(role, [
      createPathSegment(0, Math.max(5, Math.round(rushEnd * 0.45)), [{ x: basePlayer.x, y: basePlayer.y }, settlePoint], "polyline", true),
      createHoldSegment(Math.max(5, Math.round(rushEnd * 0.45)), frameCount - 1, settlePoint, false),
    ]);
  });

  const pocketCollapseTargets: Record<string, { x: number; y: number }> = {
    DE1: { x: snapToPixel(qbSetPoint.x + xDelta(18)), y: qbSetPoint.y - 34 },
    DT1: { x: snapToPixel(qbSetPoint.x + xDelta(10)), y: qbSetPoint.y - 16 },
    DT2: { x: snapToPixel(qbSetPoint.x + xDelta(10)), y: qbSetPoint.y + 14 },
    DE2: { x: snapToPixel(qbSetPoint.x + xDelta(18)), y: qbSetPoint.y + 32 },
  };

  ["DE1", "DT1", "DT2", "DE2"].forEach((role, index) => {
    const basePlayer = findPlayer(defenseBase, role);
    if (!basePlayer) {
      return;
    }
    const target = {
      x: orientX(context.losX, role.startsWith("DE") ? -10 : -14, context.direction),
      y: basePlayer.y + (index % 2 === 0 ? -4 : 4),
    };
    defenseTracks.set(role, sack
      ? [
          createPathSegment(0, rushEnd, [{ x: basePlayer.x, y: basePlayer.y }, target], "polyline", true),
          createPathSegment(rushEnd, carrierAdvanceEnd, [target, pocketCollapseTargets[role]], "polyline", true),
          createHoldSegment(carrierAdvanceEnd, frameCount - 1, pocketCollapseTargets[role], false),
        ]
      : [
          createPathSegment(0, rushEnd, [{ x: basePlayer.x, y: basePlayer.y }, target], "polyline", true),
          createHoldSegment(rushEnd, frameCount - 1, target, false),
        ]);
  });

  const coveragePlans: Record<string, any> = {
    CB1: { matchRole: "WR1", read: { x: orientX(context.losX, 86, context.direction), y: context.centerY - 144 }, preOffsetX: 14, preOffsetY: -8, catchOffsetX: 10, catchOffsetY: -6, postOffsetX: 18, postOffsetY: -8, cutoffX: 34, cutoffY: -40, scale: 0.94, qbOffsetX: 34, qbOffsetY: -34 },
    CB2: { matchRole: "WR2", read: { x: orientX(context.losX, 86, context.direction), y: context.centerY + 142 }, preOffsetX: 14, preOffsetY: 8, catchOffsetX: 10, catchOffsetY: 8, postOffsetX: 18, postOffsetY: 24, cutoffX: 34, cutoffY: 40, scale: 0.94, qbOffsetX: 34, qbOffsetY: 34 },
    S1: { matchRole: "TE", read: { x: orientX(context.losX, 104, context.direction), y: context.centerY - 50 }, preOffsetX: 20, preOffsetY: -16, catchOffsetX: 16, catchOffsetY: -14, postOffsetX: 24, postOffsetY: -18, cutoffX: 28, cutoffY: -24, scale: 0.9, depthX: orientX(context.losX, 88, context.direction), qbOffsetX: 28, qbOffsetY: -18 },
    S2: { matchRole: "SLOT", read: { x: orientX(context.losX, 104, context.direction), y: context.centerY + 46 }, preOffsetX: 20, preOffsetY: 16, catchOffsetX: 16, catchOffsetY: 14, postOffsetX: 24, postOffsetY: 18, cutoffX: 28, cutoffY: 24, scale: 0.9, depthX: orientX(context.losX, 88, context.direction), qbOffsetX: 28, qbOffsetY: 18 },
    LB1: { matchRole: "TE", read: { x: orientX(context.losX, 50, context.direction), y: context.centerY - 68 }, preOffsetX: 10, preOffsetY: -14, catchOffsetX: 12, catchOffsetY: -16, postOffsetX: 20, postOffsetY: -22, cutoffX: 22, cutoffY: -24, scale: 0.88, qbOffsetX: 18, qbOffsetY: -20 },
    LB2: { matchRole: "QB", read: { x: orientX(context.losX, 52, context.direction), y: context.centerY - 4 }, preOffsetX: 10, preOffsetY: 0, catchOffsetX: 12, catchOffsetY: 8, postOffsetX: 18, postOffsetY: 8, cutoffX: 20, cutoffY: 0, scale: 0.9, qbOffsetX: 16, qbOffsetY: 0 },
    LB3: { matchRole: "RB", read: { x: orientX(context.losX, 50, context.direction), y: context.centerY + 64 }, preOffsetX: 10, preOffsetY: 14, catchOffsetX: 12, catchOffsetY: 16, postOffsetX: 20, postOffsetY: 22, cutoffX: 22, cutoffY: 24, scale: 0.88, qbOffsetX: 18, qbOffsetY: 20 },
  };

  Object.entries(coveragePlans).forEach(([role, planSpec]: [string, any]) => {
    planSpec.scale *= clampRange(passPresentation.coverageScale ?? 1, 0.9, 1.08);
    if (context.play.coverageShell === "two-high" || context.play.coverageShell === "quarters") {
      if (role.startsWith("S")) {
        planSpec.read.x = orientX(context.losX, 118, context.direction);
        planSpec.depthX = orientX(context.losX, 102, context.direction);
        planSpec.scale *= 0.96;
      } else if (role.startsWith("CB")) {
        planSpec.preOffsetX += 6;
        planSpec.catchOffsetX += 4;
        planSpec.cutoffX += 6;
        planSpec.scale *= 0.96;
      } else if (role.startsWith("LB")) {
        planSpec.read.x = orientX(context.losX, role === "LB2" ? 60 : 56, context.direction);
        planSpec.scale *= 0.95;
      }
    } else {
      if (role.startsWith("S")) {
        planSpec.depthX = orientX(context.losX, context.play.coverageShell === "robber" ? 76 : 82, context.direction);
        planSpec.preOffsetX -= 3;
        planSpec.scale *= 1.02;
      } else if (role.startsWith("CB")) {
        planSpec.preOffsetX -= 2;
        planSpec.scale *= 1.02;
      }
    }

    if (pressureTiming === "trigger") {
      if (role.startsWith("LB")) {
        planSpec.read.x = orientX(context.losX, role === "LB2" ? 44 : 42, context.direction);
        planSpec.scale *= 1.06;
      } else if (role.startsWith("S") && context.play.coverageShell !== "two-high" && context.play.coverageShell !== "quarters") {
        planSpec.depthX = orientX(context.losX, 74, context.direction);
      }
    } else if (pressureTiming === "delayed" && role.startsWith("LB")) {
      planSpec.read.x = orientX(context.losX, role === "LB2" ? 62 : 58, context.direction);
      planSpec.scale *= 0.94;
    }

    if (rallyStyle === "swarm") {
      planSpec.postOffsetX = Math.max(10, planSpec.postOffsetX - 4);
      planSpec.cutoffX = Math.max(16, planSpec.cutoffX - 4);
      planSpec.scale *= 1.08;
    } else if (rallyStyle === "vice") {
      planSpec.postOffsetX += 6;
      planSpec.cutoffX += 6;
      planSpec.scale *= 0.92;
    } else if (rallyStyle === "downhill") {
      if (!role.startsWith("CB")) {
        planSpec.postOffsetX = Math.max(10, planSpec.postOffsetX - 3);
      }
      planSpec.scale *= 1.04;
    } else if (rallyStyle === "inside-out" && (role.startsWith("CB") || role.startsWith("S"))) {
      planSpec.cutoffX += 4;
    }

    if (role === primaryRole) {
      planSpec.catchOffsetY += tackleSideSign * 4;
      planSpec.postOffsetY += tackleSideSign * 3;
    } else if (role === helpRole) {
      planSpec.catchOffsetY += tackleSideSign * 2;
      planSpec.postOffsetY += tackleSideSign * 5;
    } else if (role === coverageShell.rallyRole) {
      planSpec.postOffsetY += tackleSideSign * 6;
    }
  });

  const coverageRoles = Object.keys(coveragePlans);
  const heroDefenseRoles = new Set(
    extendedMode
      ? Array.from(new Set([primaryRole, helpRole, coverageShell.rallyRole, robberRole].filter(Boolean))).slice(0, Math.max(3, passPresentation.heroPursuitCount ?? 4))
      : coverageRoles
  );
  const coverageOrder = [
    primaryRole,
    helpRole,
    robberRole,
    ...coverageRoles.filter((role) => ![primaryRole, helpRole, robberRole].includes(role)),
  ].filter((role, index, roles) => !!role && roles.indexOf(role) === index);
  const coverageState = new Map<string, { x: number; y: number }>(
    coverageRoles
      .map((role) => {
        const player = findPlayer(defenseBase, role);
        return player ? [role, { x: player.x, y: player.y }] as [string, { x: number; y: number }] : null;
      })
      .filter(Boolean) as [string, { x: number; y: number }][]
  );

  if (turnover) {
    const defenderRole = primaryRole;
    const basePlayer = findPlayer(defenseBase, defenderRole);
    if (basePlayer) {
      defenseTracks.set(defenderRole, [
        createPathSegment(0, catchFrame, [{ x: basePlayer.x, y: basePlayer.y }, pickPoint], "polyline", true),
        createPathSegment(catchFrame, carrierAdvanceEnd, postCatchPoints, "spline", true),
        createHoldSegment(carrierAdvanceEnd, frameCount - 1, postCatchPoints[postCatchPoints.length - 1], false),
      ]);
    }
  }

  const qbReleasePoint = evaluateTrack(offenseTracks.get("QB") || [], Math.max(0, releaseFrame), { x: qbBase.x, y: qbBase.y });
  const targetCatchState = evaluateTrack(offenseTracks.get(targetRole) || [], Math.max(0, catchFrame), catchPoint);
  const interceptorRole = turnover ? primaryRole : null;
  const pursuitRoles = turnover
    ? ["QB", "WR1", "WR2", "SLOT", "TE", "RB"].filter((role) => !!findPlayer(offenseBase, role))
    : [];

  // ── Track-based pursuit for interception-return tackles ────────────────
  // Physics-based chasing can't keep up: the interceptor's track is
  // time-compressed to fit frameCount, moving 6+ px/frame vs a chaser's
  // ~3.5 px/frame.  Instead, override the 3 nearest pursuit players' tracks
  // to converge on carrierEndPoint + contact offset over the same
  // catchFrame→carrierAdvanceEnd window as the interceptor.  Both arrive
  // simultaneously, the freeze fires with ≤ 8 px gap and no visible pause.
  if (turnover && terminalReason === "tackle") {
    const sortedPursuit = pursuitRoles
      .map((role) => {
        const p = findPlayer(offenseBase, role);
        return p ? { role, dist: pointDistance(p, carrierEndPoint) } : null;
      })
      .filter(Boolean)
      .sort((a: any, b: any) => a.dist - b.dist)
      .slice(0, 3);

    const pursuitOffsets = [
      { x: 1, y: 0 },
      { x: 5, y: targetHalf === "top" ? -3 : 3 },
      { x: 8, y: targetHalf === "top" ? -6 : 6 },
    ];

    sortedPursuit.forEach(({ role }: any, rankIndex: number) => {
      const basePlayer = findPlayer(offenseBase, role);
      const existingTrack = offenseTracks.get(role);
      if (!basePlayer || !existingTrack || existingTrack.length === 0) return;

      // Replace the final hold segment (which covers catchFrame onward)
      // with: hold until pursuitStart → path to arrivalPoint → hold at arrival.
      const lastSeg = existingTrack[existingTrack.length - 1];
      if (lastSeg.type !== "hold") return;

      const pursuitStart = Math.max(catchFrame, lastSeg.startFrame);
      const holdPoint = lastSeg.point;
      const arrivalOffset = pursuitOffsets[rankIndex];
      const arrivalPoint = {
        x: snapToPixel(carrierEndPoint.x + xDelta(arrivalOffset.x)),
        y: snapToPixel(carrierEndPoint.y + arrivalOffset.y),
      };

      offenseTracks.set(role, [
        ...existingTrack.slice(0, -1),
        createHoldSegment(lastSeg.startFrame, pursuitStart, holdPoint, false),
        createPathSegment(
          pursuitStart,
          carrierAdvanceEnd,
          [
            holdPoint,
            {
              x: snapToPixel(lerp(holdPoint.x, arrivalPoint.x, 0.68)),
              y: snapToPixel(lerp(holdPoint.y, arrivalPoint.y, 0.54) + (arrivalOffset.y === 0 ? 0 : Math.sign(arrivalOffset.y) * Math.max(4, Math.abs(arrivalOffset.y) * 0.9))),
            },
            arrivalPoint,
          ],
          "spline",
          true,
        ),
        createHoldSegment(carrierAdvanceEnd, frameCount - 1, arrivalPoint, false),
      ]);
    });
  }

  const passTackleRoles = [...coverageRoles, "DE1", "DT1", "DT2", "DE2"];
  const sackRoles = ["DE1", "DT1", "DT2", "DE2", "LB1", "LB2", "LB3"];
  const frames: any[] = [];
  let frozenFrame: any = null;

  const resolveCoveragePoint = (planSpec: any, offenseFrame: FieldPlayer[], qb: any, activeTarget: any, frame: number) => {
    const trackedPlayer = planSpec.matchRole === "QB"
      ? qb
      : findPlayer(offenseFrame, planSpec.matchRole) || activeTarget;
    const leveragePoint = trackedPlayer
      ? {
          x: snapToPixel(trackedPlayer.x + xDelta(planSpec.preOffsetX)),
          y: snapToPixel(trackedPlayer.y + planSpec.preOffsetY),
        }
      : planSpec.read;
    if (planSpec.depthX !== undefined) {
      leveragePoint.x = snapToPixel(depthAlignX(planSpec.depthX, leveragePoint.x));
    }
    if (frame <= dropFrames) {
      return planSpec.read;
    }
    const readWindow = Math.max(
      1,
      Math.round((Math.max(releaseFrame, dropFrames + 1) - dropFrames) / (pressureTiming === "trigger" ? 1.18 : pressureTiming === "delayed" ? 0.82 : 1))
    );
    const readBlend = clamp01((frame - dropFrames) / readWindow);
    return {
      x: snapToPixel(lerp(planSpec.read.x, leveragePoint.x, readBlend)),
      y: snapToPixel(lerp(planSpec.read.y, leveragePoint.y, readBlend)),
    };
  };

  for (let frame = 0; frame < frameCount; frame += 1) {
    if (frozenFrame) {
      frames.push(cloneLiveFrame(frozenFrame));
      continue;
    }

    const offenseFrame = clonePlayers(offenseBase);
    const defenseFrame = clonePlayers(defenseBase);

    offenseTracks.forEach((segments: any, role: string) => {
      const basePlayer = findPlayer(offenseBase, role);
      if (basePlayer) {
        const flowPlan = offenseFlowPlans.get(role);
        const state = flowPlan && frame < catchFrame
          ? evaluateFlowingTrack(segments, frame, { x: basePlayer.x, y: basePlayer.y }, flowPlan)
          : evaluateTrack(segments, frame, { x: basePlayer.x, y: basePlayer.y });
        setPlayerState(offenseFrame, role, state);
      }
    });

    defenseTracks.forEach((segments: any, role: string) => {
      const basePlayer = findPlayer(defenseBase, role);
      if (basePlayer) {
        setPlayerState(defenseFrame, role, evaluateTrack(segments, frame, { x: basePlayer.x, y: basePlayer.y }));
      }
    });

    const qb = findPlayer(offenseFrame, "QB") || qbBase;
    const activeTarget = findPlayer(offenseFrame, targetRole) || targetBase;

    coverageOrder.forEach((role: string, roleIndex: number) => {
      if (turnover && role === interceptorRole) {
        return;
      }
      const current = coverageState.get(role);
      const basePlayer = findPlayer(defenseBase, role);
      const planSpec = coveragePlans[role];
      if (!current || !basePlayer || !planSpec) {
        return;
      }

      let desired = resolveCoveragePoint(planSpec, offenseFrame, qb, activeTarget, frame);

      if (sack) {
        if (role.startsWith("LB")) {
          desired = {
            x: snapToPixel(qb.x + xDelta(planSpec.qbOffsetX)),
            y: snapToPixel(qb.y + planSpec.qbOffsetY),
          };
        }
      } else if (!scramble && frame >= releaseFrame && frame < catchFrame) {
        const catchLeverage = {
          x: snapToPixel(targetCatchState.x + xDelta(planSpec.catchOffsetX)),
          y: snapToPixel(targetCatchState.y + planSpec.catchOffsetY),
        };
        const releaseBlend = clamp01((frame - releaseFrame) / Math.max(1, catchFrame - releaseFrame));
        if (bracketRoles.has(role)) {
          const closeBlend = (role === primaryRole ? 0.76 : 0.28)
            * (rallyStyle === "vice" ? 1.08 : rallyStyle === "swarm" ? 0.96 : 1)
            * (pressureTiming === "trigger" ? 1.04 : pressureTiming === "delayed" ? 0.92 : 1);
          desired = {
            x: snapToPixel(lerp(desired.x, catchLeverage.x, closeBlend * releaseBlend)),
            y: snapToPixel(lerp(desired.y, catchLeverage.y, closeBlend * releaseBlend)),
          };
        } else if (role === robberRole) {
          const lanePoint = {
            x: snapToPixel(lerp(qbReleasePoint.x + 6, targetCatchState.x + xDelta(planSpec.catchOffsetX), 0.68)),
            y: snapToPixel(lerp(qbReleasePoint.y + 8, targetCatchState.y + planSpec.catchOffsetY, 0.68) + (targetHalf === "top" ? 10 : targetHalf === "bottom" ? -10 : 0)),
          };
          desired = {
            x: snapToPixel(lerp(desired.x, lanePoint.x, 0.18 * releaseBlend * (pressureTiming === "trigger" ? 1.16 : pressureTiming === "delayed" ? 0.86 : 1))),
            y: snapToPixel(lerp(desired.y, lanePoint.y, 0.18 * releaseBlend * (pressureTiming === "trigger" ? 1.16 : pressureTiming === "delayed" ? 0.86 : 1))),
          };
        }
      } else if ((completedPass || turnover) && frame >= catchFrame) {
        const ballCarrier = turnover
          ? (findPlayer(defenseFrame, interceptorRole!) || findPlayer(defenseBase, interceptorRole!))
          : activeTarget;
        if (ballCarrier) {
          const rallyPoint = {
            x: snapToPixel(ballCarrier.x + xDelta(planSpec.postOffsetX)),
            y: snapToPixel(ballCarrier.y + planSpec.postOffsetY),
          };
          if (deepCompletion && frame >= deepTackleStart && deepTackleRoles.includes(role)) {
            const baseTackleOffsets = role === primaryRole
              ? { x: 1, y: 0 }
              : role === helpRole
                ? { x: 6, y: targetHalf === "top" ? -4 : targetHalf === "bottom" ? 4 : 3 }
                : role === coverageShell.rallyRole
                  ? { x: 10, y: targetHalf === "top" ? -8 : targetHalf === "bottom" ? 8 : 0 }
                  : { x: 12, y: 0 };
            const tackleOffsets = {
              x: baseTackleOffsets.x * spacingMultiplier + (rallyStyle === "vice" ? 2 : rallyStyle === "swarm" ? -1 : 0),
              y: baseTackleOffsets.y * spacingMultiplier,
            };
            const tacklePoint = {
              x: snapToPixel(ballCarrier.x + xDelta(tackleOffsets.x)),
              y: snapToPixel(ballCarrier.y + tackleOffsets.y),
            };
            const deepTackleProgress = clamp01((frame - deepTackleStart) / Math.max(1, deepTackleFrames));
            const tackleBlend = (role === primaryRole ? 0.98 : role === helpRole ? 0.88 : 0.76)
              * (tackleEnvironment === "crowded" ? 1.06 : tackleEnvironment === "space" ? 0.88 : tackleEnvironment === "chaotic" ? 1.02 : 1)
              * deepTackleProgress;
            desired = {
              x: snapToPixel(lerp(desired.x, tacklePoint.x, tackleBlend)),
              y: snapToPixel(lerp(desired.y, tacklePoint.y, tackleBlend)),
            };
          } else if (deepCompletion && (role === primaryRole || role === helpRole || role === coverageShell.rallyRole)) {
            const closePoint = {
              x: snapToPixel(ballCarrier.x + xDelta(role === primaryRole ? 12 : role === helpRole ? 18 : 24)),
              y: snapToPixel(ballCarrier.y + (role === primaryRole ? 0 : role === helpRole ? (targetHalf === "top" ? -8 : targetHalf === "bottom" ? 8 : 5) : (targetHalf === "top" ? -16 : targetHalf === "bottom" ? 16 : 0))),
            };
            const closeBlend = (role === primaryRole ? 0.74 : role === helpRole ? 0.54 : 0.34)
              * (rallyStyle === "downhill" ? 1.08 : rallyStyle === "vice" ? 0.92 : 1)
              * (tackleEnvironment === "crowded" ? 1.04 : tackleEnvironment === "space" ? 0.9 : 1);
            desired = {
              x: snapToPixel(lerp(desired.x, closePoint.x, closeBlend)),
              y: snapToPixel(lerp(desired.y, closePoint.y, closeBlend)),
            };
          } else if (commitRoles.has(role)) {
            const rallyBlend = (role === primaryRole ? 0.82 : role === helpRole ? 0.42 : 0.28)
              * (rallyStyle === "swarm" ? 1.22 : rallyStyle === "downhill" ? 1.12 : rallyStyle === "vice" ? 0.82 : 0.96)
              * (tackleEnvironment === "crowded" ? 1.08 : tackleEnvironment === "space" ? 0.88 : 1);
            desired = {
              x: snapToPixel(lerp(desired.x, rallyPoint.x, rallyBlend)),
              y: snapToPixel(lerp(desired.y, rallyPoint.y, rallyBlend)),
            };
          } else if (role === robberRole) {
            desired = {
              x: snapToPixel(lerp(desired.x, rallyPoint.x, 0.2 * (rallyStyle === "swarm" ? 1.18 : rallyStyle === "vice" ? 0.88 : 1))),
              y: snapToPixel(lerp(desired.y, rallyPoint.y, 0.18 * (rallyStyle === "swarm" ? 1.18 : rallyStyle === "vice" ? 0.88 : 1))),
            };
          } else {
            const cutoffPoint = {
              x: snapToPixel(ballCarrier.x + xDelta(planSpec.cutoffX)),
              y: snapToPixel(ballCarrier.y + planSpec.cutoffY),
            };
            desired = {
              x: snapToPixel(lerp(desired.x, cutoffPoint.x, 0.26 * (rallyStyle === "vice" ? 1.08 : 0.96))),
              y: snapToPixel(lerp(desired.y, cutoffPoint.y, 0.22 * (rallyStyle === "vice" ? 1.08 : 0.96))),
            };
          }

          if (extendedMode && !heroDefenseRoles.has(role)) {
            const corridorPoint = {
              x: snapToPixel(ballCarrier.x + xDelta(planSpec.cutoffX * (passPresentation.backgroundDepthScale ?? 0.84) + 12 + roleIndex * 3)),
              y: snapToPixel(ballCarrier.y + planSpec.cutoffY * (passPresentation.backgroundDepthScale ?? 0.84) * 0.7 + (roleIndex % 2 === 0 ? -1 : 1) * 8 * pursuitSpread),
            };
            desired = {
              x: snapToPixel(lerp(desired.x, corridorPoint.x, 0.34)),
              y: snapToPixel(lerp(desired.y, corridorPoint.y, 0.3)),
            };
          }
        }
      } else if (scramble && frame >= scrambleCommitFrame) {
        const scrambleCarrier = qb;
        const chasePoint = {
          x: snapToPixel(scrambleCarrier.x + xDelta(planSpec.postOffsetX)),
          y: snapToPixel(scrambleCarrier.y + planSpec.postOffsetY),
        };
        if (scrambleTackleRoles.includes(role)) {
          const closePoint = role === primaryRole
            ? { x: snapToPixel(scrambleCarrier.x + xDelta(8)), y: snapToPixel(scrambleCarrier.y) }
            : role === helpRole
              ? { x: snapToPixel(scrambleCarrier.x + xDelta(14)), y: snapToPixel(scrambleCarrier.y + (targetHalf === "top" ? -6 : targetHalf === "bottom" ? 6 : 3)) }
              : role === coverageShell.rallyRole
                ? { x: snapToPixel(scrambleCarrier.x + xDelta(18)), y: snapToPixel(scrambleCarrier.y + (targetHalf === "top" ? -10 : targetHalf === "bottom" ? 10 : 0)) }
                : { x: snapToPixel(scrambleCarrier.x + xDelta(planSpec.cutoffX * 0.55)), y: snapToPixel(scrambleCarrier.y + planSpec.cutoffY * 0.55) };
          const chaseBlend = (role === primaryRole ? 0.66 : role === helpRole ? 0.44 : 0.28)
            * (rallyStyle === "swarm" ? 1.18 : rallyStyle === "vice" ? 0.84 : 1)
            * (tackleEnvironment === "crowded" ? 1.06 : tackleEnvironment === "space" ? 0.9 : 1);
          desired = {
            x: snapToPixel(lerp(desired.x, closePoint.x, chaseBlend)),
            y: snapToPixel(lerp(desired.y, closePoint.y, chaseBlend)),
          };
        } else {
          desired = {
            x: snapToPixel(lerp(desired.x, chasePoint.x, 0.18)),
            y: snapToPixel(lerp(desired.y, chasePoint.y, 0.16)),
          };
        }
      } else if (incomplete && frame >= catchFrame) {
        desired = {
          x: snapToPixel(lerp(desired.x, targetCatchState.x + xDelta(planSpec.catchOffsetX), 0.18)),
          y: snapToPixel(lerp(desired.y, targetCatchState.y + planSpec.catchOffsetY, 0.18)),
        };
      }

      if ((completedPass && frame >= catchFrame) || (scramble && frame >= scrambleCommitFrame)) {
        const pursuitSign = role === primaryRole
          ? (targetHalf === "top" ? -1 : targetHalf === "bottom" ? 1 : Math.sign(planSpec.postOffsetY || planSpec.cutoffY || planSpec.catchOffsetY || 1))
          : role === helpRole
            ? Math.sign(planSpec.cutoffY || planSpec.postOffsetY || 1)
            : Math.sign(planSpec.catchOffsetY || planSpec.cutoffY || ((roleIndex % 2 === 0) ? -1 : 1));
        desired = curvedApproachTarget(current, desired, pursuitSign, {
          nearDistance: deepCompletion ? 10 : 16,
          farDistance: deepCompletion ? 72 : 88,
          minOffset: deepCompletion ? 3 : 5,
          maxOffset: role === primaryRole ? 12 : role === helpRole ? 16 : 14,
          scale: rallyStyle === "vice" ? 0.88 : rallyStyle === "swarm" ? 1.08 : 1,
        });
      }

      const peerPositions = coverageOrder
        .slice(0, roleIndex)
        .map((peerRole: string) => findPlayer(defenseFrame, peerRole))
        .filter(Boolean);
      const deepClosePhase = deepCompletion && frame >= deepTackleStart && deepTackleRoles.includes(role);
      const spacedDesired = frame >= dropFrames
        ? spreadTargetAwayFromPeers(
            desired,
            peerPositions as any[],
            deepClosePhase
              ? Math.max(6, roleSpacingDistance(role, "pass") * 0.42 * spacingMultiplier)
              : roleSpacingDistance(role, "pass") * spacingMultiplier,
            deepClosePhase ? 0.08 : frame >= catchFrame ? 0.24 : 0.18
          )
        : desired;

      const family = sack || (scramble && frame >= scrambleCommitFrame) ? "run" : "pass";
      const pursuitScaleBase = deepClosePhase
        ? Math.min(1.32, planSpec.scale + (role === primaryRole ? 0.3 : role === helpRole ? 0.24 : 0.16))
        : deepCompletion && (role === primaryRole || role === helpRole || role === coverageShell.rallyRole)
          ? Math.min(1.16, planSpec.scale + (role === primaryRole ? 0.14 : 0.1))
          : planSpec.scale;
      const pursuitScale = Math.min(
        1.38,
        pursuitScaleBase
          * rallyPursuitMultiplier
          * (role === primaryRole ? 1 + arrivalJitterFrames * 0.03 : role === helpRole ? 1 - arrivalJitterFrames * 0.02 : 1)
          * (extendedMode && !heroDefenseRoles.has(role) ? 0.84 : 1)
          * (tackleEnvironment === "crowded" ? 1.04 : tackleEnvironment === "space" ? 0.94 : 1)
      );
      const next = movePointTowards(current, spacedDesired, playerPixelsPerFrame(basePlayer.speedRating || speedRatingForRole(context.defense, role), family, pursuitScale));
      coverageState.set(role, next);
      setPlayerState(defenseFrame, role, { ...next, moving: true });
    });

    const snapBall = centerSnapBallPoint(centerBase, context.direction, context.losX, context.centerY - 6);
    const qbBall = carriedBallPoint(qb, orientX(context.losX, -40, context.direction), context.centerY - 8, context.direction);
    let ballX = qbBall.ballX;
    let ballY = qbBall.ballY;
    if (frame < snapFrameCount) {
      const snapBallPoint = blendBallPoint(snapBall, qbBall, frame / Math.max(1, snapFrameCount));
      ballX = snapBallPoint.ballX;
      ballY = snapBallPoint.ballY;
    } else if (sack || scramble) {
      ballX = qbBall.ballX;
      ballY = qbBall.ballY;
    } else if (frame < releaseFrame) {
      ballX = qbBall.ballX;
      ballY = qbBall.ballY;
    } else if (frame <= catchFrame) {
      const flightAmount = clamp01((frame - releaseFrame) / Math.max(1, catchFrame - releaseFrame));
      ballX = snapToPixel(lerp(qbReleasePoint.x + 6, targetCatchState.x + (passPresentation.throwLeadX ?? 6), flightAmount));
      ballY = snapToPixel(
        lerp(qbReleasePoint.y + 8 + (passPresentation.throwLeadY ?? 0), targetCatchState.y + 8, flightAmount)
        - Math.sin(flightAmount * Math.PI) * (passPresentation.throwArcHeight ?? 54)
      );
    } else if (turnover) {
      const defender = findPlayer(defenseFrame, interceptorRole!) || findPlayer(defenseBase, interceptorRole!);
      const carryBall = carriedBallPoint(defender, targetCatchState.x, targetCatchState.y, -context.direction);
      ballX = carryBall.ballX;
      ballY = carryBall.ballY;
    } else if (incomplete) {
      ballX = targetCatchState.x + 6;
      ballY = targetCatchState.y + 22;
    } else {
      const carryBall = carriedBallPoint(activeTarget, targetCatchState.x, targetCatchState.y, context.direction);
      ballX = carryBall.ballX;
      ballY = carryBall.ballY;
    }

    let shouldFreeze = false;
    if (incomplete) {
      shouldFreeze = frame >= Math.min(frameCount - 1, catchFrame + 4);
    } else if (sack) {
      const nearestRusher = nearestPlayerToPoint(defenseFrame, qb, sackRoles);
      shouldFreeze = frame >= dropFrames && !!nearestRusher && nearestRusher.distance <= (pressureTiming === "trigger" ? 24 : pressureTiming === "delayed" ? 18 : 20);
    } else if (scramble) {
      const nearestDefender = nearestPlayerToPoint(defenseFrame, qb, scrambleTackleRoles);
      if (terminalReason === "touchdown") {
        shouldFreeze = frame >= carrierAdvanceEnd - 1 && ballBreaksPlane(ballX, context.goalLineX, context.direction, 2);
      } else if (terminalReason === "out-of-bounds") {
        shouldFreeze = frame >= carrierAdvanceEnd - 1 && (Math.abs(qb.y - carrierEndPoint.y) <= 10 || spotReached(qb, carrierEndPoint, 14));
      } else {
        shouldFreeze = frame >= carrierAdvanceEnd - 1
          && spotReached(qb, carrierEndPoint, 14)
          && !!nearestDefender
          && nearestDefender.distance <= (tackleEnvironment === "space" ? 20 : tackleEnvironment === "crowded" ? 16 : 18);
      }
    } else if (completedPass) {
      if (terminalReason === "touchdown") {
        shouldFreeze = frame >= carrierAdvanceEnd - 1 && ballBreaksPlane(ballX, context.goalLineX, context.direction, 2);
      } else if (terminalReason === "out-of-bounds") {
        shouldFreeze = frame >= carrierAdvanceEnd - 1 && (Math.abs(activeTarget.y - carrierEndPoint.y) <= 10 || spotReached(activeTarget, carrierEndPoint, 14));
      } else {
        const tackleRoles = deepCompletion ? deepTackleRoles : passTackleRoles;
        const nearestDefender = nearestPlayerToPoint(defenseFrame, activeTarget, tackleRoles);
        const catchSettled = !deepCompletion || frame >= deepTackleStart;
        shouldFreeze = catchSettled
          && frame >= catchFrame
          && spotReached(activeTarget, carrierEndPoint, deepCompletion ? 8 : 16)
          && !!nearestDefender
          && nearestDefender.distance <= (deepCompletion ? (tackleEnvironment === "space" ? 10 : tackleEnvironment === "crowded" ? 7 : 8) : (tackleEnvironment === "space" ? 22 : tackleEnvironment === "crowded" ? 16 : 20));
      }
    } else if (turnover) {
      const interceptor = findPlayer(defenseFrame, interceptorRole!) || findPlayer(defenseBase, interceptorRole!);
      if (interceptor) {
        if (terminalReason === "touchdown") {
          shouldFreeze = frame >= carrierAdvanceEnd - 1 && ballBreaksPlane(ballX, carrierGoalLineX, -context.direction, 2);
        } else if (terminalReason === "out-of-bounds") {
          shouldFreeze = frame >= carrierAdvanceEnd - 1 && (Math.abs(interceptor.y - carrierEndPoint.y) <= 10 || spotReached(interceptor, carrierEndPoint, 14));
        } else {
          const nearestPursuer = pursuitRoles.length ? nearestPlayerToPoint(offenseFrame, interceptor, pursuitRoles) : null;
          shouldFreeze = frame >= carrierAdvanceEnd - 1
            && spotReached(interceptor, carrierEndPoint, 14)
            && (!nearestPursuer || nearestPursuer.distance <= 14);
        }
      }
    }
    if (!shouldFreeze && frame >= frameCount - 1) {
      if (incomplete) {
        shouldFreeze = true;
      } else if (sack) {
        const nearestRusher = nearestPlayerToPoint(defenseFrame, qb, sackRoles);
        shouldFreeze = !!nearestRusher && nearestRusher.distance <= (pressureTiming === "trigger" ? 28 : pressureTiming === "delayed" ? 22 : 24);
      } else if (scramble) {
        const nearestDefender = nearestPlayerToPoint(defenseFrame, qb, scrambleTackleRoles);
        shouldFreeze = terminalReason === "touchdown" || terminalReason === "out-of-bounds"
          ? true
          : !!nearestDefender && nearestDefender.distance <= (tackleEnvironment === "space" ? 24 : tackleEnvironment === "crowded" ? 18 : 22) && spotReached(qb, carrierEndPoint, 16);
      } else if (completedPass) {
        if (terminalReason === "touchdown" || terminalReason === "out-of-bounds") {
          shouldFreeze = true;
        } else {
          const tackleRoles = deepCompletion ? deepTackleRoles : passTackleRoles;
          const nearestDefender = nearestPlayerToPoint(defenseFrame, activeTarget, tackleRoles);
          shouldFreeze = spotReached(activeTarget, carrierEndPoint, deepCompletion ? 10 : 18)
            && !!nearestDefender
            && nearestDefender.distance <= (deepCompletion ? (tackleEnvironment === "space" ? 12 : tackleEnvironment === "crowded" ? 8 : 10) : (tackleEnvironment === "space" ? 26 : tackleEnvironment === "crowded" ? 20 : 24));
        }
      } else if (turnover) {
        const interceptor = findPlayer(defenseFrame, interceptorRole!) || findPlayer(defenseBase, interceptorRole!);
        if (terminalReason === "tackle" && pursuitRoles.length) {
          const nearestPursuer = interceptor ? nearestPlayerToPoint(offenseFrame, interceptor, pursuitRoles) : null;
          shouldFreeze = !!interceptor && spotReached(interceptor, carrierEndPoint, 16) && !!nearestPursuer && nearestPursuer.distance <= 18;
        } else {
          shouldFreeze = !!interceptor && (terminalReason === "touchdown" || terminalReason === "out-of-bounds" || spotReached(interceptor, carrierEndPoint, 16));
        }
      }
    }

    const takeoverFrames = interception ? frame - catchFrame : -1;
    const ribbonMessage = interceptionCallout && takeoverFrames >= 0 && takeoverFrames < 18
      ? interceptionCallout
      : null;
    const takeawayFlash = interception && interceptorRole && takeoverFrames >= 0 && takeoverFrames < 12
      ? {
          role: interceptorRole,
          teamType: "defense",
          intensity: clamp01(1 - (takeoverFrames / 12)),
          color: interceptionCallout?.color,
        }
      : null;
    const liveFrame: any = shouldFreeze
      ? freezeLiveFrame(offenseFrame, defenseFrame, { ballX, ballY })
      : { offensePlayers: offenseFrame, defensePlayers: defenseFrame, ball: { ballX, ballY } };
    if (ribbonMessage) {
      liveFrame.ribbonMessage = ribbonMessage;
    }
    if (takeawayFlash) {
      liveFrame.takeawayFlash = takeawayFlash;
    }
    if (shouldFreeze) {
      frozenFrame = liveFrame;
    }
    frames.push(liveFrame);
  }

  if (!frozenFrame && (completedPass || scramble) && terminalReason === "tackle" && frames.length && (scramble ? scrambleTackleRoles.length : tackleFinishRoles.length)) {
    const finishCarrierRole = scramble ? "QB" : targetRole;
    const finishCarrierBase = scramble ? qbBase : targetBase;
    const finishTackleRoles = scramble ? scrambleTackleRoles : tackleFinishRoles;
    let carryFrame = cloneLiveFrame(frames[frames.length - 1]);
    const initialCarrier = findPlayer(carryFrame.offensePlayers, finishCarrierRole) || findPlayer(carryFrame.offensePlayers, "WR1") || finishCarrierBase;
    const initialNearestDefender = initialCarrier
      ? nearestPlayerToPoint(carryFrame.defensePlayers, initialCarrier, finishTackleRoles)
      : null;
    const maxFinishFrames = finishFrameBudget(
      initialNearestDefender?.distance || (deepCompletion ? 34 : scramble ? 28 : 26),
      deepCompletion ? (tackleEnvironment === "space" ? 26 : 22) : scramble ? 18 : 16,
      deepCompletion ? 2.2 : scramble ? 2.4 : 2.5,
      deepCompletion ? (extendedMode ? 12 : 10) : scramble ? 10 : 8
    );

    for (let extraFrame = 0; extraFrame < maxFinishFrames && !frozenFrame; extraFrame += 1) {
      const offenseFrame = clonePlayers(carryFrame.offensePlayers);
      const defenseFrame = clonePlayers(carryFrame.defensePlayers);
      const carrier = findPlayer(offenseFrame, finishCarrierRole) || findPlayer(offenseFrame, "WR1") || finishCarrierBase;
      if (!carrier) {
        break;
      }

      finishTackleRoles.forEach((role: string, roleIndex: number) => {
        const defender = findPlayer(defenseFrame, role);
        const basePlayer = findPlayer(defenseBase, role) || defender;
        if (!defender || !basePlayer) {
          return;
        }

        const offset = role === primaryRole
          ? { x: 1, y: tackleSideSign * 1 }
          : role === helpRole
            ? { x: 5, y: (targetHalf === "top" ? -3 : targetHalf === "bottom" ? 3 : 2) + tackleSideSign * 2 }
            : role === coverageShell.rallyRole
              ? { x: 8, y: (targetHalf === "top" ? -6 : targetHalf === "bottom" ? 6 : 0) + tackleSideSign * 3 }
              : { x: 10, y: tackleSideSign * 2 };
        let desired: { x: number; y: number } = {
          x: snapToPixel(carrier.x + xDelta(offset.x * spacingMultiplier * (passPresentation.fallForwardScale ?? 1))),
          y: snapToPixel(carrier.y + offset.y * spacingMultiplier),
        };
        const priorPeers = tackleFinishRoles
          .slice(0, roleIndex)
          .map((peerRole: string) => findPlayer(defenseFrame, peerRole))
          .filter(Boolean);
        desired = spreadTargetAwayFromPeers(desired, priorPeers as any[], (role === primaryRole ? 4 : 6) * spacingMultiplier, 0.04);
        const step = Math.min(
          role === primaryRole ? 3.1 : role === helpRole ? 2.9 : 2.7,
          playerPixelsPerFrame(
            basePlayer.speedRating || speedRatingForRole(context.defense, role),
            "pass",
            (role === primaryRole ? 0.82 : role === helpRole ? 0.78 : 0.74) * rallyPursuitMultiplier
          )
        );
        const next = movePointTowards({ x: defender.x, y: defender.y }, desired, step);
        setPlayerState(defenseFrame, role, { ...next, moving: true });
      });

      const ballCarrier = findPlayer(offenseFrame, finishCarrierRole) || findPlayer(offenseFrame, "WR1") || finishCarrierBase;
      const nearestDefender = ballCarrier ? nearestPlayerToPoint(defenseFrame, ballCarrier, finishTackleRoles) : null;
      const closeEnough = !!ballCarrier && !!nearestDefender && nearestDefender.distance <= (
        deepCompletion
          ? (tackleEnvironment === "space" ? 10 : tackleEnvironment === "crowded" ? 7 : 8)
          : scramble
            ? (tackleEnvironment === "space" ? 12 : tackleEnvironment === "crowded" ? 9 : 10)
            : (tackleEnvironment === "space" ? 12 : tackleEnvironment === "crowded" ? 9 : 10)
      );
      const ball = ballCarrier
        ? carriedBallPoint(ballCarrier, carryFrame.ball.ballX, carryFrame.ball.ballY, context.direction)
        : carryFrame.ball;
      const liveFrame = closeEnough
        ? freezeLiveFrame(offenseFrame, defenseFrame, ball)
        : { offensePlayers: offenseFrame, defensePlayers: defenseFrame, ball };
      if (closeEnough) {
        frozenFrame = liveFrame;
      }
      frames.push(liveFrame);
      carryFrame = liveFrame;
    }

    if (!frozenFrame) {
      const forcedFrame = cloneLiveFrame(frames[frames.length - 1]);
      const offenseFrame = clonePlayers(forcedFrame.offensePlayers);
      const defenseFrame = clonePlayers(forcedFrame.defensePlayers);
      const carrier = findPlayer(offenseFrame, finishCarrierRole) || findPlayer(offenseFrame, "WR1") || finishCarrierBase;
      const forceRole = finishTackleRoles[0];
      const defender = forceRole ? findPlayer(defenseFrame, forceRole) : null;
      if (carrier && defender) {
        setPlayerState(defenseFrame, forceRole, {
          x: snapToPixel(carrier.x + xDelta(1)),
          y: snapToPixel(carrier.y + tackleSideSign),
          moving: true,
        });
        const finalBall = carriedBallPoint(carrier, carryFrame.ball.ballX, carryFrame.ball.ballY, context.direction);
        const finalFrame = freezeLiveFrame(offenseFrame, defenseFrame, finalBall);
        frames[frames.length - 1] = finalFrame;
        frozenFrame = finalFrame;
      }
    }
  }

  if (!frozenFrame && turnover && terminalReason === "tackle" && frames.length && pursuitRoles.length) {
    const lastDefense = frames[frames.length - 1].defensePlayers;
    const lastInterceptor = findPlayer(lastDefense, interceptorRole!);
    if (lastInterceptor) {
      const sortedPursuers = pursuitRoles
        .map((role) => {
          const p = findPlayer(frames[frames.length - 1].offensePlayers, role);
          return p ? { role, distance: pointDistance(p, lastInterceptor) } : null;
        })
        .filter(Boolean)
        .sort((a: any, b: any) => a.distance - b.distance)
        .slice(0, 3);
      const activePursuitRoles = sortedPursuers.map((e: any) => e.role);
      let carryFrame = cloneLiveFrame(frames[frames.length - 1]);
      const initialNearestPursuer = activePursuitRoles.length
        ? nearestPlayerToPoint(carryFrame.offensePlayers, lastInterceptor, activePursuitRoles)
        : null;
      const maxFinishFrames = finishFrameBudget(
        initialNearestPursuer?.distance || 34,
        20,
        2.35,
        10
      );

      for (let extraFrame = 0; extraFrame < maxFinishFrames && !frozenFrame; extraFrame += 1) {
        const offenseFrame = clonePlayers(carryFrame.offensePlayers);
        const defenseFrame = clonePlayers(carryFrame.defensePlayers);
        const interceptor = findPlayer(defenseFrame, interceptorRole!);
        if (!interceptor) {
          break;
        }

        activePursuitRoles.forEach((role: string, roleIndex: number) => {
          const chaser = findPlayer(offenseFrame, role);
          const basePlayer = findPlayer(offenseBase, role) || chaser;
          if (!chaser || !basePlayer) {
            return;
          }
          const offsets = [
            { x: 1, y: 0 },
            { x: 5, y: targetHalf === "top" ? -3 : 3 },
            { x: 8, y: targetHalf === "top" ? -6 : 6 },
          ];
          const offset = offsets[roleIndex] || { x: 10, y: 0 };
          const desired = {
            x: snapToPixel(interceptor.x + xDelta(offset.x)),
            y: snapToPixel(interceptor.y + offset.y),
          };
          const priorPeers = activePursuitRoles
            .slice(0, roleIndex)
            .map((r: string) => findPlayer(offenseFrame, r))
            .filter(Boolean);
          const spacedDesired = spreadTargetAwayFromPeers(desired, priorPeers as any[], 6, 0.06);
          const step = Math.min(
            roleIndex === 0 ? 3.1 : 2.8,
            playerPixelsPerFrame(basePlayer.speedRating || 75, "pass", roleIndex === 0 ? 0.82 : 0.74)
          );
          const next = movePointTowards({ x: chaser.x, y: chaser.y }, spacedDesired, step);
          setPlayerState(offenseFrame, role, { ...next, moving: true });
        });

        const nearestPursuer = nearestPlayerToPoint(offenseFrame, interceptor, activePursuitRoles);
        const closeEnough = !!nearestPursuer && nearestPursuer.distance <= 10;
        const ball = carriedBallPoint(interceptor, carryFrame.ball.ballX, carryFrame.ball.ballY, -context.direction);
        const liveFrame = closeEnough
          ? freezeLiveFrame(offenseFrame, defenseFrame, ball)
          : { offensePlayers: offenseFrame, defensePlayers: defenseFrame, ball };
        if (closeEnough) {
          frozenFrame = liveFrame;
        }
        frames.push(liveFrame);
        carryFrame = liveFrame;
      }

      if (!frozenFrame) {
        const forcedFrame = cloneLiveFrame(frames[frames.length - 1]);
        const offenseFrame = clonePlayers(forcedFrame.offensePlayers);
        const defenseFrame = clonePlayers(forcedFrame.defensePlayers);
        const interceptor = findPlayer(defenseFrame, interceptorRole!);
        const forceRole = activePursuitRoles[0];
        const chaser = forceRole ? findPlayer(offenseFrame, forceRole) : null;
        if (interceptor && chaser) {
          setPlayerState(offenseFrame, forceRole, {
            x: snapToPixel(interceptor.x + xDelta(1)),
            y: snapToPixel(interceptor.y),
            moving: true,
          });
          const finalBall = carriedBallPoint(interceptor, carryFrame.ball.ballX, carryFrame.ball.ballY, -context.direction);
          const finalFrame = freezeLiveFrame(offenseFrame, defenseFrame, finalBall);
          frames[frames.length - 1] = finalFrame;
          frozenFrame = finalFrame;
        }
      }
    }
  }

  return frames;
}
